// frontend/src/components/GuestAlertBanner.js
//
// A guest ordered food or asked for something from the guest page. The bell and
// the SMS can both be missed, so this sits across the top of the screen, chimes
// again every minute, and can only be removed by CLICKING it: the click says "I
// have seen this and it is being worked on". It is acknowledged for everyone (the
// front desk, the waiters and housekeeping all see the same item; the server
// clears every role's copy and keeps the clicker's name), so one person taking
// it stops the rest of the building being nagged, and the Notifications history
// shows who took it and when.
//
// Rows are the 'guest_alert' notifications visible to the signed-in role
// (worker/lib/guestPortal.js); nothing here decides who gets what. Short-polls
// every 15 seconds: slower than the bell's 30 would be a long time for a guest
// waiting on their food. Keeps polling while the tab is hidden, like the bell, so
// the chime still reaches someone working in another window.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BellRing } from 'lucide-react';
import { apiFetch } from '../api/client';
import { playAlert, primeAudio } from '../lib/notificationSound';

const POLL_MS = 15000;
const RECHIME_MS = 60000;

export default function GuestAlertBanner({ onOpenTab }) {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const seenRef = useRef(new Set());
  const primedRef = useRef(false);
  const lastChimeRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/notifications?category=guest_alert&status=open&limit=20');
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (!data.success) return;
      const rows = (data.notifications || []).slice().sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
      const fresh = rows.some((n) => !seenRef.current.has(n.id));
      rows.forEach((n) => seenRef.current.add(n.id));
      setItems(rows);
      // Chime for a new arrival (not for what was already waiting when the page
      // loaded), and again each minute while anything is still unclaimed.
      const now = Date.now();
      if (rows.length > 0 && ((fresh && primedRef.current) || now - lastChimeRef.current >= RECHIME_MS)) {
        try { playAlert('critical'); } catch { /* audio unavailable */ }
        lastChimeRef.current = now;
      }
      primedRef.current = true;
    } catch { /* network blip: keep what is on screen */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const unlock = () => primeAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => {
      clearInterval(t);
      window.removeEventListener('pointerdown', unlock);
    };
  }, [load]);

  const take = async (n) => {
    setBusyId(n.id);
    try {
      const res = await apiFetch(`/notifications/${n.id}/acknowledge`, { method: 'POST' });
      if (res.ok) {
        setItems((xs) => xs.filter((x) => x.id !== n.id));
        if (n.link_tab && typeof onOpenTab === 'function') onOpenTab(n.link_tab, n.link_entity);
      }
    } catch { /* still on screen: it can be clicked again */ } finally {
      setBusyId(null);
      load();
    }
  };

  if (items.length === 0) return null;

  return (
    <div role="alert" aria-live="assertive" className="w-full">
      {items.map((n) => (
        <button
          key={n.id}
          type="button"
          onClick={() => take(n)}
          disabled={busyId === n.id}
          className="w-full text-left flex items-center gap-3 px-4 py-3 bg-red-600 hover:bg-red-700 text-white border-b border-red-800 animate-pulse disabled:opacity-70"
        >
          <BellRing className="w-5 h-5 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1 text-sm sm:text-base">
            <strong>{n.title}</strong>
            {n.body ? <span className="opacity-90"> {'—'} {n.body}</span> : null}
          </span>
          <span className="shrink-0 rounded bg-white/20 px-3 py-1 text-xs sm:text-sm font-semibold">
            Tap: seen, I am on it
          </span>
        </button>
      ))}
    </div>
  );
}
